/* Cart Milestones Bar — v22.4
   Updates:
   - Automatically fetches product tags if missing from /cart.js
   - Caches tags in memory for faster subsequent refreshes
   - Free items require BOTH "boho" tag and 0 price
   - Includes debug logging for each cart item
   - No flicker + optimized refresh lifecycle
*/

(function () {
  "use strict";

  const BAR_ID = "cart-milestones-bar";
  const PROGRESS_ID = "milestones-progress";
  const STORAGE_KEY = "cart_milestones_state_v22_4";
  const TAG_CACHE = {}; // memory cache { handle: [tags] }

  const $ = (s) => document.querySelector(s);
  const $$ = (s) => Array.from(document.querySelectorAll(s));
  const $bar = () => document.getElementById(BAR_ID);
  const $progress = () => document.getElementById(PROGRESS_ID);
  const fetchCart = () =>
    fetch("/cart.js", { credentials: "same-origin" })
      .then((r) => r.json())
      .catch(() => null);

  /* ---------- Helpers ---------- */
  function setPct(p) {
    const el = $progress();
    if (el) el.style.width = `${Math.min(100, Math.max(0, p))}%`;
  }

  function paintSteps(done) {
    $$(`#${BAR_ID} .step`).forEach((s) =>
      s.classList.toggle("completed", done.has(+s.dataset.stepIndex))
    );
  }

  function formatRemainingAmount(state, threshold) {
    const remainCents = Math.max(0, threshold * 100 - state.subtotalCents);
    const remain = remainCents / 100;
    return `$${remain % 1 === 0 ? remain.toFixed(0) : remain.toFixed(2).replace(/0$/, "")}`;
  }

  function updateShippingText(msgEl, remainUsd) {
    if (!msgEl) return;
    if (!msgEl.dataset.template) msgEl.dataset.template = msgEl.innerHTML;
    const tpl = msgEl.dataset.template;
    msgEl.innerHTML = tpl.replace(/\[amount\]/gi, `<span class="amount">${remainUsd}</span>`);
  }

  function toggleMilestoneMessage(state) {
    const el = document.getElementById("milestone-message");
    if (!el) return;
    const shouldShow = state.totalEligible >= 3 && state.freebiesPresent === 0;
    el.classList.toggle("hidden", !shouldShow);
  }

  /* ---------- Shipping message ---------- */
  function paintShippingMessage(state) {
    const host = document.getElementById("shipping-message");
    if (!host) return;

    const threshold = Number(host.closest(`#${BAR_ID}`)?.dataset.cartFreeShippingThreshold || 0);
    const msgAway = host.querySelector('.msg[data-state="away"]');
    const msgFree = host.querySelector('.msg[data-state="free"]');
    const msgFreePlus = host.querySelector('.msg[data-state="free_plus"]');
    [msgAway, msgFree, msgFreePlus].forEach((n) => n && n.setAttribute("hidden", "hidden"));

    host.style.visibility = "hidden";

    const remainUsd = formatRemainingAmount(state, threshold);

    if (state.totalEligible === 0 || state.subtotalCents < threshold * 100) {
      msgAway?.removeAttribute("hidden");
      updateShippingText(msgAway, remainUsd);
      host.style.visibility = "visible";
      toggleMilestoneMessage(state);
      return;
    }

    if (state.freebiesPresent > 0) {
      msgFreePlus?.removeAttribute("hidden");
    } else {
      msgFree?.removeAttribute("hidden");
    }

    host.style.visibility = "visible";
    toggleMilestoneMessage(state);
  }

  function completeSet(total, freebies) {
    const d = new Set();
    if (total >= 1) d.add(1);
    if (total >= 2) d.add(2);
    if (total >= 4) d.add(4);
    if (total >= 5) d.add(5);
    if (freebies >= 1) d.add(3);
    if (freebies >= 2) d.add(6);
    return d;
  }

  /* ---------- Tag fetch helper with cache ---------- */
  async function fetchProductTags(handle) {
    if (!handle) return [];
    if (TAG_CACHE[handle]) return TAG_CACHE[handle]; // return from cache

    try {
      const r = await fetch(`/products/${handle}.js`);
      if (!r.ok) return [];
      const data = await r.json();
      const tags = (data.tags || []).map((t) => String(t).toLowerCase());
      TAG_CACHE[handle] = tags; // cache it
      return tags;
    } catch (err) {
      console.warn("Failed to fetch tags for", handle, err);
      return [];
    }
  }

  /* ---------- Build state (with tag fetch fallback) ---------- */
  async function buildState(cart) {
    let total = 0,
      freebies = 0;

    console.groupCollapsed("🧩 Cart Milestones Debug");

    for (const it of cart?.items || []) {
      const q = Number(it.quantity) || 0;
      if (!q) continue;
      total += q;

      let tags = Array.isArray(it.product?.tags)
        ? it.product.tags.map((t) => String(t).toLowerCase())
        : [];

      if (tags.length === 0 && it.handle) {
        tags = await fetchProductTags(it.handle);
      }

      const isFreeItem = Number(it.final_price) === 0 && tags.includes("bogo");
      if (isFreeItem) freebies += q;

      console.log({
        title: it.title,
        handle: it.handle,
        price: it.final_price,
        qty: q,
        tags,
        isFreeItem,
      });
    }

    console.groupEnd();

    freebies = Math.min(2, freebies);

    const subtotalCents = (cart?.items || []).reduce(
      (sum, it) => sum + (Number(it.final_line_price) || 0),
      0
    );

    const completed = completeSet(total, freebies);

    return {
      subtotalCents,
      totalEligible: total,
      freebiesPresent: freebies,
      completed,
    };
  }

  /* ---------- Step geometry ---------- */
  function getAnchors() {
    const track = $bar()?.querySelector(".progress-bar");
    const steps = $$(`#${BAR_ID} .step`);
    if (!track || !steps.length) return null;

    const rect = track.getBoundingClientRect();
    const left = rect.left;
    const width = rect.width || 1;

    return steps.map((step) => {
      const r = step.getBoundingClientRect();
      const cx = (r.left + r.right) / 2;
      return ((cx - left) / width) * 100;
    });
  }

  function computePercentToStep(total) {
    const anchors = getAnchors();
    if (!anchors || !anchors.length) return 0;
    const totalSteps = anchors.length;
    if (total <= 0) return 0;
    if (total >= totalSteps) return 100;
    const idx = Math.min(total - 1, totalSteps - 1);
    return anchors[idx] ?? 0;
  }

  /* ---------- Refresh & paint ---------- */
  async function refresh() {
    const el = $bar();
    if (!el) return;
    const cart = await fetchCart();
    if (!cart) return;

    const state = await buildState(cart);

    // --- 🧩 Logic fix: if 3 items but no freebies, treat as 4 for progress
    let displayTotal = state.totalEligible;
    if (state.totalEligible === 3 && state.freebiesPresent === 0) {
      displayTotal = 4;
    }

    requestAnimationFrame(() => {
      const pct = computePercentToStep(displayTotal);
      setPct(pct);
      paintSteps(state.completed);
      paintShippingMessage(state);
    });

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ totalEligible: state.totalEligible, ts: Date.now() })
    );
  }

  function hydrate() {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return;
    try {
      const s = JSON.parse(data);
      setPct(s.totalEligible ? computePercentToStep(s.totalEligible) : 0);
    } catch {}
  }

  /* ---------- Lifecycle ---------- */
  function wire() {
    hydrate();
    refresh();

    const events = [
      "cart:change",
      "cart:updated",
      "cart:refresh",
      "cart-drawer:refreshed",
      "theme:cart:update",
    ];
    events.forEach((e) => document.addEventListener(e, refresh, { passive: true }));

    document.addEventListener("change", (e) => {
      if (e.target.matches?.(".quantity-selector__input")) setTimeout(refresh, 250);
    });
    document.addEventListener("click", (e) => {
      if (e.target.closest?.(".quantity-selector__button")) setTimeout(refresh, 250);
    });

    const drawer = document.querySelector("cart-drawer, #CartDrawer, .drawer");
    if (drawer) {
      const obs = new MutationObserver(() => {
        clearTimeout(window.__milestoneDelay);
        window.__milestoneDelay = setTimeout(refresh, 300);
      });
      obs.observe(drawer, { childList: true, subtree: true });
    }

    ["pageshow", "resize", "orientationchange"].forEach((evt) =>
      window.addEventListener(evt, () => {
        hydrate();
        refresh();
      })
    );
  }

  document.addEventListener("DOMContentLoaded", wire);
})();